import type { Arrayable } from '@vueuse/core';
import type { PostProcessStage, PostProcessStageCollection } from 'cesium';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { MaybeRefOrAsyncGetter } from '../toPromiseValue';
import { computedAsync } from '@vueuse/core';
import { toValue, watchEffect } from 'vue';
import { toPromiseValue } from '../toPromiseValue';
import { useViewer } from '../useViewer';

export interface UsePostProcessStageOptions {
  /**
   * The collection of PostProcessStage to be added
   * @default useViewer().scene.postProcessStages
   */
  collection?: PostProcessStageCollection;

  /**
   * Whether to destroy the stage when removed from the collection.
   * When true, the stage's GPU resources will be released.
   * @default true
   */
  destroyOnRemove?: boolean;

  /**
   * default value of `isActive`
   * @default true
   */
  isActive?: MaybeRefOrGetter<boolean>;

  /**
   * Ref passed to receive the updated of async evaluation
   */
  evaluating?: Ref<boolean>;
}

/**
 * Add `PostProcessStage` to the `PostProcessStageCollection`, automatically update when the data changes, and destroy the side effects caused by the previous `PostProcessStage`.
 *
 * Overload 1: Parameter supports passing in a single value.
 */
export function usePostProcessStage<T extends PostProcessStage = PostProcessStage>(
  stage?: MaybeRefOrAsyncGetter<T | undefined>,
  options?: UsePostProcessStageOptions,
): ComputedRef<T | undefined>;

/**
 * Add `PostProcessStage` to the `PostProcessStageCollection`, automatically update when the data changes, and destroy the side effects caused by the previous `PostProcessStage`.
 *
 * Overload 2: Parameter supports passing in an array.
 */
export function usePostProcessStage<T extends PostProcessStage = PostProcessStage>(
  stages?: MaybeRefOrAsyncGetter<Array<T | undefined> | undefined>,
  options?: UsePostProcessStageOptions,
): ComputedRef<T[] | undefined>;

export function usePostProcessStage<T extends PostProcessStage>(
  data?: MaybeRefOrAsyncGetter<Arrayable<T | undefined>>,
  options: UsePostProcessStageOptions = {},
) {
  const {
    collection,
    destroyOnRemove = true,
    isActive = true,
    evaluating,
  } = options;

  const result = computedAsync(
    () => toPromiseValue(data),
    undefined,
    {
      evaluating,
    },
  );

  const viewer = useViewer();

  watchEffect((onCleanup) => {
    if (!viewer.value) {
      return;
    }
    const _isActive = toValue(isActive);
    if (_isActive) {
      const list = Array.isArray(result.value) ? [...result.value] : [result.value];
      const _collection = collection ?? viewer.value.scene.postProcessStages;

      list.forEach(item => (item && _collection.add(item)));
      onCleanup(() => {
        list.forEach((item) => {
          if (item) {
            _collection.remove(item);
            if (destroyOnRemove && typeof item.destroy === 'function' && !item.isDestroyed()) {
              item.destroy();
            }
          }
        });
      });
    }
  });

  return result;
}
